import React, { useId } from 'react';
import { TagDto } from '../../types/admin.types';
import { Video } from '../../types/video.types';
import {
    formatVideoCardDate,
    formatVideoCardFightNumber,
    formatVideoCardParticipantName,
    orderVideoCardParticipants
} from '../../utils/video-card';
import styles from './SearchDiscoveryPanels.module.scss';

/**
 * Minimal tag shape needed by reusable search discovery surfaces.
 */
export type DiscoveryTag = Pick<TagDto, 'id' | 'name'>;

type RailBreakpoint = 'none' | '1280' | '1440';

interface SearchDiscoveryPanelsProps {
    videos?: Video[];
    tags?: DiscoveryTag[];
    fighters?: string[];
    events?: string[];
    onVideoSelect?: (video: Video) => void;
    onTagSelect?: (tag: DiscoveryTag) => void;
    onFighterSelect?: (fighter: string) => void;
    onEventSelect?: (eventName: string) => void;
    onViewAll?: () => void;
    showVideos?: boolean;
    showViewAll?: boolean;
    viewAllLabel?: string;
    railBreakpoint?: RailBreakpoint;
    mobileMetaFirst?: boolean;
    className?: string;
}

interface SearchDiscoverySkeletonProps {
    className?: string;
}

const getVideoThumbnail = (video: Video) => video.ThumbnailBase64
    ? `data:image/png;base64,${video.ThumbnailBase64}`
    : null;

const getVideoMatchup = (video: Video) => {
    const orderedParticipants = orderVideoCardParticipants(
        (video.Participants ?? []).map((participant) => ({
            name: participant.ParticipantName,
            cornerColor: participant.CornerColor ?? null
        }))
    );
    const leftName = formatVideoCardParticipantName(orderedParticipants[0]?.name);
    const rightName = formatVideoCardParticipantName(orderedParticipants[1]?.name);

    if (leftName === 'TBD' && rightName === 'TBD') {
        return null;
    }

    return `${leftName} vs ${rightName}`;
};

const getVideoMetadata = (video: Video) => {
    const metaItems = [
        formatVideoCardFightNumber(video.FightNumber),
        formatVideoCardDate(video.EventDate),
        video.EventName,
        video.LocationName
    ].filter((value) => Boolean(value && value.trim())) as string[];

    return metaItems.join(' | ');
};

const hasItems = (items?: unknown[]) => Boolean(items && items.length > 0);

/**
 * Shared discovery panel used by search surfaces to render videos, tags, fighters, and events.
 */
const SearchDiscoveryPanels = ({
    videos = [],
    tags = [],
    fighters = [],
    events = [],
    onVideoSelect,
    onTagSelect,
    onFighterSelect,
    onEventSelect,
    onViewAll,
    showVideos = true,
    showViewAll = true,
    viewAllLabel = 'View all results',
    railBreakpoint = 'none',
    mobileMetaFirst = false,
    className
}: SearchDiscoveryPanelsProps) => {
    const id = useId();
    const hasVideos = showVideos && videos.length > 0;
    const panelsClassName = [
        styles.panels,
        hasVideos ? styles.hasVideos : '',
        railBreakpoint === '1280' ? styles.railAt1280 : '',
        railBreakpoint === '1440' ? styles.railAt1440 : '',
        mobileMetaFirst ? styles.metaFirstOnMobile : '',
        className ?? ''
    ].filter(Boolean).join(' ');

    return (
        <div className={panelsClassName}>
            {hasVideos ? (
                <section
                    className={`${styles.suggestionGroup} ${styles.videoResultsGroup}`}
                    aria-labelledby={`${id}-videos`}
                >
                    <h3 className={styles.groupTitle} id={`${id}-videos`}>Top results</h3>
                    {videos.map((video) => {
                        const thumbnail = getVideoThumbnail(video);
                        const matchup = getVideoMatchup(video);
                        const metadata = getVideoMetadata(video);

                        return (
                            <button
                                key={video.Id || video.Url}
                                type="button"
                                className={styles.videoButton}
                                aria-label={`Watch ${video.Title}`}
                                onClick={() => onVideoSelect?.(video)}
                            >
                                <span className={styles.thumbnail}>
                                    {thumbnail ? (
                                        <img src={thumbnail} alt="" loading="lazy" />
                                    ) : (
                                        <span className={styles.thumbnailPlaceholder}>
                                            <span className="material-symbols-outlined" aria-hidden="true">sports_mma</span>
                                        </span>
                                    )}
                                    <span className={styles.playBadge} aria-hidden="true">
                                        <span className="material-symbols-outlined">play_arrow</span>
                                    </span>
                                </span>
                                <span className={styles.videoCopy}>
                                    <span className={styles.videoTitle}>{video.Title}</span>
                                    {matchup ? <span className={styles.matchup}>{matchup}</span> : null}
                                    {metadata ? <span className={styles.metadata}>{metadata}</span> : null}
                                </span>
                            </button>
                        );
                    })}
                </section>
            ) : null}

            {hasItems(tags) ? (
                <section
                    className={`${styles.suggestionGroup} ${styles.metaResultsGroup}`}
                    aria-labelledby={`${id}-tags`}
                >
                    <h3 className={styles.groupTitle} id={`${id}-tags`}>Tags</h3>
                    <div className={styles.tagGrid}>
                        {tags.map((tag) => (
                            <button
                                key={tag.id}
                                type="button"
                                className={styles.tagButton}
                                onClick={() => onTagSelect?.(tag)}
                            >
                                #{tag.name}
                            </button>
                        ))}
                    </div>
                </section>
            ) : null}

            {hasItems(fighters) ? (
                <section
                    className={`${styles.suggestionGroup} ${styles.metaResultsGroup}`}
                    aria-labelledby={`${id}-fighters`}
                >
                    <h3 className={styles.groupTitle} id={`${id}-fighters`}>Fighters</h3>
                    {fighters.map((fighter) => (
                        <button
                            key={fighter}
                            type="button"
                            className={styles.textButton}
                            onClick={() => onFighterSelect?.(fighter)}
                        >
                            <span className={styles.textButtonLabel}>{fighter}</span>
                            <span className={styles.textButtonMeta}>View fights</span>
                        </button>
                    ))}
                </section>
            ) : null}

            {hasItems(events) ? (
                <section
                    className={`${styles.suggestionGroup} ${styles.metaResultsGroup}`}
                    aria-labelledby={`${id}-events`}
                >
                    <h3 className={styles.groupTitle} id={`${id}-events`}>Events</h3>
                    {events.map((eventName) => (
                        <button
                            key={eventName}
                            type="button"
                            className={styles.textButton}
                            onClick={() => onEventSelect?.(eventName)}
                        >
                            <span className={styles.textButtonLabel}>{eventName}</span>
                            <span className={styles.textButtonMeta}>View event videos</span>
                        </button>
                    ))}
                </section>
            ) : null}

            {showViewAll && onViewAll ? (
                <button
                    type="button"
                    className={`${styles.viewAllButton} ${styles.metaAction}`}
                    onClick={onViewAll}
                >
                    {viewAllLabel}
                </button>
            ) : null}
        </div>
    );
};

/**
 * Structured skeleton matching the shared search discovery panel layout.
 */
export const SearchDiscoverySkeleton = ({ className }: SearchDiscoverySkeletonProps) => (
    <div className={[styles.loadingState, className ?? ''].filter(Boolean).join(' ')} aria-label="Loading search suggestions">
        <section className={styles.skeletonGroup} aria-hidden="true">
            <div className={styles.skeletonTitle} />
            <div className={styles.videoSkeletonRow}>
                <span className={styles.skeletonThumb} />
                <span className={styles.skeletonCopy}>
                    <span className={styles.skeletonLine} />
                    <span className={styles.skeletonLineShort} />
                    <span className={styles.skeletonLineTiny} />
                </span>
            </div>
            <div className={styles.videoSkeletonRow}>
                <span className={styles.skeletonThumb} />
                <span className={styles.skeletonCopy}>
                    <span className={styles.skeletonLine} />
                    <span className={styles.skeletonLineShort} />
                    <span className={styles.skeletonLineTiny} />
                </span>
            </div>
        </section>
        <section className={styles.skeletonGroup} aria-hidden="true">
            <div className={styles.skeletonTitle} />
            <div className={styles.tagSkeletonGrid}>
                <span className={styles.tagSkeleton} />
                <span className={styles.tagSkeleton} />
                <span className={styles.tagSkeleton} />
                <span className={styles.tagSkeleton} />
            </div>
        </section>
        <section className={styles.skeletonGroup} aria-hidden="true">
            <div className={styles.skeletonTitle} />
            <div className={styles.textSkeletonRow} />
            <div className={styles.textSkeletonRow} />
        </section>
    </div>
);

export default SearchDiscoveryPanels;
